import type { TowerDesign } from "@/game/towerSpec";
import type { Section } from "@/physics/model";

/**
 * WOODSIDE OMEGA TOWER (VLF Transmitter Woodside, Victoria, Australia) — a 432 m
 * grounded triangular guyed steel lattice mast carrying an umbrella antenna, the
 * tallest structure in the Southern Hemisphere until its demolition on 22 April
 * 2015 (completed 1982). Built from published data (Wikipedia: VLF Transmitter
 * Woodside):
 *   - 432 m guyed lattice mast, grounded (not a "hot" insulated tower).
 *   - Umbrella antenna: the topmost downward-sloping guys are the radiating
 *     elements; lower guys simply stay the mast.
 *
 * Modelled as a slim triangular guyed mast with support-guy rings up the height
 * plus a wide top "umbrella" ring. A 432 m mast is well beyond the sim's tuned
 * range and will likely sway/collapse — accuracy to the design is the goal.
 */

const MAST_LEG: Section = { name: "Omega mast leg (tube)", A: 0.008, I: 2e-3, dragWidth: 0.18 };
const OMEGA_GUY: Section = { name: "Omega guy strand", A: 6e-4, I: 2e-7, dragWidth: 0.03 };

const H = 432;
// support guy levels [height m, anchor radius m], anchor radius growing with height
const guyLevels: Array<[number, number]> = [
  [54, 90],
  [108, 140],
  [162, 185],
  [216, 225],
  [270, 260],
  [324, 290],
  [378, 315],
  [420, 340], // topmost = the umbrella radiators sloping down to distant anchors
];

export const design: TowerDesign = {
  id: "omega-woodside",
  name: "Woodside Omega Tower (Australia)",
  description: "432 m grounded guyed VLF mast w/ umbrella antenna — tallest in the S. Hemisphere; razed 2015.",
  author: "iconic tower — from published spec (VLF Transmitter Woodside)",
  spec: {
    type: "guyed",
    height: H,
    stack: {
      faces: 3, // triangular mast
      panels: 115, // 9 m panels
      baseRadius: 1.5, // ~2.6 m face
      topRadius: 1.5, // constant section
      legSection: MAST_LEG,
      braceSection: "angleL5",
      legMaterial: "a36",
      braceMaterial: "a36",
      bracing: "X",
    },
    guys: guyLevels.map(([h, anchorRadius]) => ({
      atHeightFrac: h / H,
      anchorRadius,
      section: OMEGA_GUY,
      material: "guyStrand",
      pretensionStrain: 0.001,
    })),
    foundation: {
      soil: "stiff-clay", // Gippsland plain
      base: { type: "pad", width: 4, diameter: 1, depth: 3, concrete: "c20" },
      anchor: { type: "deadman", width: 5, diameter: 1, depth: 3.5, concrete: "c20" },
    },
  },
};
